// Deliverables tab — extracted from the legacy index.tsx monolith.
//
// Reads shared data (competition, deliverables, teamSubmissions,
// teamStatuses, myTeam) from useCompetitionShell. Owns the filter chip
// state, the upload flow, and the "final submission" confirm gate locally
// since they're tab-scoped. _layout.tsx keeps the shell + chrome mounted
// so this file is just a body.

import * as DocumentPicker from "expo-document-picker";
import { useLocalSearchParams } from "expo-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { Linking, Platform, ScrollView, Text, View } from "react-native";

import { COLORS, FONTS } from "../../../../../lib/brand";
import { PANE_THEMES } from "../../../../../components/brand/paneThemes";

const STUDENT_THEME = PANE_THEMES.Student;

import { CompactWorkspaceHero } from "../../../../../components/student/dashboard/CompactWorkspaceHero";
import { DeliverableDropZone } from "../../../../../components/student/DeliverableDropZone";
import {
  DeliverableFilterChips,
  type DeliverableFilterCounts,
  type DeliverableFilterKey,
} from "../../../../../components/student/dashboard/DeliverableFilterChips";
import { Padlock } from "../../../../../components/student/TeamControlIcons";
import { Badge, Button, Card, EmptyState } from "../../../../../components/ui";
import { useCompetitionShell } from "../../../../../components/student/CompetitionShellContext";
import { formatDeadlineLong } from "../../../../../lib/datetime";
import {
  getDeliverableTemplateSignedUrl,
} from "../../../../../lib/deliverables";
import {
  documentPickerTypesForFileType,
  getSubmissionDownloadUrl,
  uploadSubmission,
} from "../../../../../lib/submissions";
import type {
  Deliverable,
  DeliverableFileType,
  TeamDeliverableStatusRow,
} from "../../../../../types/database";

function firstStr(v: string | string[] | undefined): string | undefined {
  if (v == null) return undefined;
  return Array.isArray(v) ? v[0] : v;
}

function formatDueCanvas(iso: string | null): string {
  if (!iso) return "No due date";
  const long = formatDeadlineLong(iso);
  if (long === "—") return "No due date";
  return `Due ${long}`;
}

function emptyStateForFilter(filter: DeliverableFilterKey): string {
  switch (filter) {
    case "active":
      return "Nothing to work on right now — every open deliverable is in.";
    case "in_review":
      return "No submissions waiting on the judges right now.";
    case "complete":
      return "No deliverables have been graded yet.";
    case "locked":
      return "No locked deliverables — every gate so far has passed.";
    default:
      return "No deliverables match this filter.";
  }
}

function fileTypeBadgeLabel(fileType: DeliverableFileType): string {
  if (fileType === "excel") return "Excel";
  if (fileType === "powerpoint") return "PowerPoint";
  if (fileType === "pdf") return "PDF";
  if (fileType === "word") return "Word";
  if (fileType === "csv") return "CSV";
  if (fileType === "zip") return "ZIP";
  if (fileType === "image") return "Image";
  if (fileType === "video") return "Video";
  return "Any";
}

function isDeliverableOpen(d: Deliverable): boolean {
  if (d.opens_at == null || d.opens_at === "") return true;
  const t = new Date(d.opens_at).getTime();
  if (Number.isNaN(t)) return true;
  return t <= Date.now();
}

function formatScore(score: number | null): string {
  if (score == null) return "—";
  return score.toFixed(2);
}

function renderDeliverableStatusPill(
  statusRow: TeamDeliverableStatusRow | undefined,
): { text: string; status: "success" | "amber" | "grey" | "red" } | null {
  if (!statusRow) return null;
  if (statusRow.status === "not_submitted") return null;

  if (statusRow.is_locked_gate) {
    if (statusRow.status === "passed") {
      return {
        text: `Gate passed ✓ (avg ${formatScore(statusRow.avg_score)}, ${statusRow.judge_count} judges)`,
        status: "success",
      };
    }
    if (statusRow.status === "failed") {
      return {
        text: "Gate failed — eliminated at this stage",
        status: "red",
      };
    }
    return {
      text: "Awaiting judge",
      status: "amber",
    };
  }

  if (statusRow.status === "failed") {
    return {
      text: "Below scoring threshold",
      status: "red",
    };
  }
  return {
    text: `Scored: ${formatScore(statusRow.avg_score)} (${statusRow.judge_count} judges)`,
    status: "grey",
  };
}

async function openInNewTab(url: string) {
  if (Platform.OS === "web") {
    window.open(url, "_blank", "noopener,noreferrer");
  } else {
    await Linking.openURL(url);
  }
}

export default function StudentCompetitionDeliverablesScreen() {
  const {
    competitionId,
    competition,
    deliverables,
    teamSubmissions,
    teamStatuses,
    myTeam,
    busy,
    setBusy,
    actionErr,
    setActionErr,
    refresh,
  } = useCompetitionShell();

  const params = useLocalSearchParams<{ deliverable?: string | string[] }>();
  const deliverableFocus = firstStr(params.deliverable);

  const scrollRef = useRef<ScrollView>(null);
  const [deliverablesFilter, setDeliverablesFilter] =
    useState<DeliverableFilterKey>("all");
  const [justUploaded, setJustUploaded] = useState<{
    deliverableId: string;
    at: number;
  } | null>(null);
  const [confirmFinalUploadFor, setConfirmFinalUploadFor] =
    useState<Deliverable | null>(null);

  const submissionByDeliverableId = useMemo(() => {
    const m = new Map<string, (typeof teamSubmissions)[number]>();
    for (const s of teamSubmissions) m.set(s.deliverable_id, s);
    return m;
  }, [teamSubmissions]);

  const statusByDeliverableId = useMemo(() => {
    const m = new Map<string, TeamDeliverableStatusRow>();
    for (const s of teamStatuses) m.set(s.deliverable_id, s);
    return m;
  }, [teamStatuses]);

  const deliverableBuckets = useMemo(() => {
    const sorted = [...deliverables].sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0));
    const out = new Map<string, DeliverableFilterKey>();
    for (const d of sorted) {
      const sub = submissionByDeliverableId.get(d.id);
      const stat = statusByDeliverableId.get(d.id);
      const blocked = sorted.some(
        (g) =>
          g.is_locked_gate &&
          g.sequence < d.sequence &&
          (statusByDeliverableId.get(g.id)?.status ?? "not_submitted") !== "passed",
      );
      if (blocked) {
        out.set(d.id, "locked");
        continue;
      }
      if (stat?.status === "passed") {
        out.set(d.id, "complete");
        continue;
      }
      if (sub) {
        out.set(d.id, "in_review");
        continue;
      }
      out.set(d.id, "active");
    }
    return out;
  }, [deliverables, submissionByDeliverableId, statusByDeliverableId]);

  const deliverableFilterCounts: DeliverableFilterCounts = useMemo(() => {
    const counts: DeliverableFilterCounts = {
      all: deliverables.length,
      active: 0,
      in_review: 0,
      complete: 0,
      locked: 0,
    };
    for (const d of deliverables) {
      const b = deliverableBuckets.get(d.id);
      if (b && b !== "all") counts[b] += 1;
    }
    return counts;
  }, [deliverables, deliverableBuckets]);

  const filteredDeliverables = useMemo(() => {
    const sorted = [...deliverables].sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0));
    if (deliverablesFilter === "all") return sorted;
    return sorted.filter((d) => deliverableBuckets.get(d.id) === deliverablesFilter);
  }, [deliverables, deliverableBuckets, deliverablesFilter]);

  // Auto-scroll to ?deliverable=<id> when present (deep-link from dashboard).
  useEffect(() => {
    if (!deliverableFocus || deliverables.length === 0) return;
    const idx = deliverables.findIndex((d) => d.id === deliverableFocus);
    if (idx < 0) return;
    const t = setTimeout(() => {
      if (Platform.OS === "web" && typeof document !== "undefined") {
        document.getElementById(`student-deliverable-${deliverableFocus}`)?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
        return;
      }
      const roughY = Math.max(0, 520 + idx * 420);
      scrollRef.current?.scrollTo({ y: roughY, animated: true });
    }, 450);
    return () => clearTimeout(t);
  }, [deliverableFocus, deliverables]);

  async function uploadAssetForDeliverable(
    d: Deliverable,
    asset: DocumentPicker.DocumentPickerAsset,
  ) {
    if (!competitionId || !myTeam?.team.id) return;
    setBusy(true);
    setActionErr(null);
    try {
      const { error } = await uploadSubmission({
        deliverableId: d.id,
        teamId: myTeam.team.id,
        competitionId,
        deliverableFileType: d.file_type,
        dueAtIso: d.due_at,
        file: asset,
      });
      if (error) {
        setActionErr(error.message);
        return;
      }
      await refresh();
      const stamp = Date.now();
      setJustUploaded({ deliverableId: d.id, at: stamp });
      setTimeout(() => {
        setJustUploaded((prev) => (prev && prev.at === stamp ? null : prev));
      }, 5000);
    } finally {
      setBusy(false);
    }
  }

  async function pickAndUploadDeliverable(d: Deliverable) {
    if (!competitionId || !myTeam?.team.id) return;
    const types = documentPickerTypesForFileType(d.file_type);
    const result = await DocumentPicker.getDocumentAsync({
      type: types,
      copyToCacheDirectory: true,
    });
    if (result.canceled || !result.assets?.[0]) return;
    await uploadAssetForDeliverable(d, result.assets[0]);
  }

  function uploadDroppedFileForDeliverable(d: Deliverable, file: File) {
    if (!competitionId || !myTeam?.team.id) return;
    const asset = {
      uri: typeof URL !== "undefined" ? URL.createObjectURL(file) : "",
      name: file.name,
      size: file.size,
      mimeType: file.type || "application/octet-stream",
      file,
    } as unknown as DocumentPicker.DocumentPickerAsset;
    void uploadAssetForDeliverable(d, asset);
  }

  async function onOpenSubmissionFile(filePath: string) {
    const { url, error } = await getSubmissionDownloadUrl(filePath);
    if (error || !url) {
      setActionErr(error?.message ?? "Could not open file");
      return;
    }
    await openInNewTab(url);
  }

  if (!competition) return null;
  const row = competition;

  return (
    <ScrollView
      ref={scrollRef}
      className="flex-1 pb-10"
      keyboardShouldPersistTaps="handled"
    >
      <CompactWorkspaceHero
        companyName={row.company_name ?? "Company"}
        programName={row.name}
        opensAt={row.opens_at}
        closesAt={row.closes_at}
        status={row.status}
        teamSizeMin={row.team_size_min}
        teamSizeMax={row.max_team_size}
        deliverablesCount={deliverables.length}
        submittedCount={teamSubmissions.length}
        nextDueAt={
          [...deliverables]
            .filter((d) => !teamSubmissions.some((s) => s.deliverable_id === d.id))
            .sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0))[0]?.due_at ?? null
        }
      />

      {deliverables.length > 0 ? (
        <View className="px-4 py-6 md:px-8">
          <View style={{ gap: 14 }}>
            <View
              style={{
                flexDirection: "row",
                flexWrap: "wrap",
                alignItems: "flex-end",
                justifyContent: "space-between",
                gap: 16,
                marginBottom: 16,
              }}
            >
              <View style={{ minWidth: 240, flex: 1 }}>
                <Text
                  className="text-[10px] font-bold uppercase tracking-widest-3 text-amber"
                  style={{ fontFamily: FONTS.bodyBold, letterSpacing: 2 }}
                >
                  Deliverables
                </Text>
                <Text
                  className="mt-1 text-[28px] leading-tight text-text"
                  style={{ fontFamily: FONTS.display, letterSpacing: -0.2 }}
                >
                  What to{" "}
                  <Text
                    style={{
                      fontStyle: "italic",
                      color: COLORS.AMBER.hex,
                      fontFamily: FONTS.display,
                    }}
                  >
                    submit.
                  </Text>
                </Text>
              </View>
              <DeliverableFilterChips
                active={deliverablesFilter}
                counts={deliverableFilterCounts}
                onChange={setDeliverablesFilter}
              />
            </View>

            {actionErr ? (
              <Text className="mb-1 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
                {actionErr}
              </Text>
            ) : null}

            {filteredDeliverables.length === 0 ? (
              <View
                className="rounded-md border px-4 py-8"
                style={{
                  borderColor: STUDENT_THEME.border,
                  backgroundColor: "rgba(255,255,255,0.55)",
                  alignItems: "center",
                }}
              >
                <Text
                  className="text-[13px] text-text/55"
                  style={{ fontFamily: FONTS.body }}
                >
                  {emptyStateForFilter(deliverablesFilter)}
                </Text>
              </View>
            ) : null}

            {filteredDeliverables.map((d) => {
              const sub = submissionByDeliverableId.get(d.id);
              const statusRow = statusByDeliverableId.get(d.id);
              const open = isDeliverableOpen(d);
              const priorGates = deliverables
                .filter((x) => x.sequence < d.sequence && x.is_locked_gate)
                .sort((a, b) => a.sequence - b.sequence);
              let blockingGate: Deliverable | undefined;
              for (let i = priorGates.length - 1; i >= 0; i--) {
                const gate = priorGates[i];
                const gateStatus = statusByDeliverableId.get(gate.id);
                if (gateStatus?.status !== "passed") {
                  blockingGate = gate;
                  break;
                }
              }
              const canUpload = Boolean(myTeam?.team.id) && open && !blockingGate;
              const ft = d.file_type as DeliverableFileType;
              const statusPill = renderDeliverableStatusPill(statusRow);
              const dueAtMs = d.due_at ? new Date(d.due_at).getTime() : null;
              const isPastDue =
                dueAtMs != null && !Number.isNaN(dueAtMs) && Date.now() > dueAtMs;
              const isGraded =
                statusRow?.status === "passed" || statusRow?.status === "failed";
              const canResubmit =
                canUpload && d.allow_revisions !== false && !isPastDue && !isGraded;

              return (
                <DeliverableDropZone
                  key={d.id}
                  enabled={canUpload && d.allow_revisions !== false}
                  onFile={(f) => uploadDroppedFileForDeliverable(d, f)}
                  fileTypeHint={ft.toUpperCase()}
                >
                  <View
                    nativeID={Platform.OS === "web" ? `student-deliverable-${d.id}` : undefined}
                    collapsable={false}
                    style={{ position: "relative" }}
                  >
                    <View
                      aria-hidden={blockingGate && Platform.OS === "web" ? true : undefined}
                      style={
                        blockingGate && Platform.OS === "web"
                          ? ({
                              filter: "blur(7px)",
                              userSelect: "none",
                              opacity: 0.85,
                            } as unknown as never)
                          : undefined
                      }
                    >
                      <Card variant="light" padding="md">
                        <View className="min-w-0 flex-1">
                          <Text
                            className="text-[19px] leading-tight text-text"
                            style={{ fontFamily: FONTS.displayMedium }}
                          >
                            {d.sequence}. {d.name}
                          </Text>
                          <View className="mt-2 flex-row flex-wrap items-center gap-2">
                            <Badge status="info">{fileTypeBadgeLabel(ft)}</Badge>
                            {d.is_locked_gate ? <Badge status="amber">Locked gate</Badge> : null}
                            {statusPill ? <Badge status={statusPill.status}>{statusPill.text}</Badge> : null}
                          </View>
                        </View>
                        {d.description ? (
                          <Text
                            className="mt-3 text-[13px] leading-5 text-text/80"
                            style={{ fontFamily: FONTS.body }}
                          >
                            {d.description}
                          </Text>
                        ) : null}
                        <View className="mt-3 flex-row flex-wrap items-center gap-x-3 gap-y-1">
                          <Text
                            className="text-[12px] text-text/65"
                            style={{ fontFamily: FONTS.bodyMedium }}
                          >
                            {formatDueCanvas(d.due_at)}
                          </Text>
                          <Text
                            className="text-[12px] text-text/35"
                            style={{ fontFamily: FONTS.body }}
                          >
                            ·
                          </Text>
                          <Text
                            className="text-[12px] text-text/65"
                            style={{ fontFamily: FONTS.bodyMedium }}
                          >
                            {Boolean(d.is_scored) && d.point_value != null
                              ? `${d.point_value} pts`
                              : "Not scored"}
                          </Text>
                        </View>
                        {d.template_url ? (
                          <Text
                            className="mt-2 text-sm text-amber underline"
                            onPress={async () => {
                              const { url, error: uErr } = await getDeliverableTemplateSignedUrl(
                                d.template_url,
                              );
                              if (uErr || !url) {
                                setActionErr(uErr?.message ?? "Could not open template");
                                return;
                              }
                              await openInNewTab(url);
                            }}
                          >
                            Download template
                          </Text>
                        ) : null}
                        <View className="mt-3 border-t border-sand-dark pt-3">
                          {!sub ? (
                            <View>
                              <View className="self-start">
                                <Badge status="grey">Pending</Badge>
                              </View>
                              {canUpload ? (
                                <View className="mt-2 self-start">
                                  <Button
                                    size="sm"
                                    variant="primary"
                                    onPress={() => {
                                      if (d.allow_revisions === false) {
                                        setConfirmFinalUploadFor(d);
                                      } else {
                                        void pickAndUploadDeliverable(d);
                                      }
                                    }}
                                    disabled={busy}
                                    leftIcon={
                                      <Text style={{ color: COLORS.LINEN.hex, fontSize: 12, marginRight: 1 }}>
                                        ↑
                                      </Text>
                                    }
                                  >
                                    Upload
                                  </Button>
                                  {confirmFinalUploadFor?.id === d.id ? (
                                    <View
                                      className="mt-2.5 rounded-md border px-3 py-3"
                                      style={{
                                        borderColor: "rgba(192,57,43,0.35)",
                                        backgroundColor: "rgba(192,57,43,0.06)",
                                        maxWidth: 360,
                                      }}
                                    >
                                      <Text
                                        className="text-[10px] font-bold uppercase"
                                        style={{
                                          fontFamily: FONTS.bodyBold,
                                          color: STUDENT_THEME.destructive,
                                          letterSpacing: 1.4,
                                        }}
                                      >
                                        Final submission
                                      </Text>
                                      <Text
                                        className="mt-1 text-[12.5px] leading-[18px]"
                                        style={{
                                          fontFamily: FONTS.body,
                                          color: "rgba(28,43,26,0.8)",
                                        }}
                                      >
                                        This deliverable doesn&apos;t allow revisions. Once you upload, you
                                        can&apos;t replace the file. Make sure it&apos;s the version you want
                                        judged.
                                      </Text>
                                      <View className="mt-3 flex-row gap-2">
                                        <Button
                                          size="sm"
                                          variant="ghost-light"
                                          onPress={() => setConfirmFinalUploadFor(null)}
                                          disabled={busy}
                                        >
                                          Cancel
                                        </Button>
                                        <Button
                                          size="sm"
                                          variant="primary"
                                          onPress={() => {
                                            const target = confirmFinalUploadFor;
                                            setConfirmFinalUploadFor(null);
                                            if (target) void pickAndUploadDeliverable(target);
                                          }}
                                          disabled={busy}
                                        >
                                          Pick file & upload
                                        </Button>
                                      </View>
                                    </View>
                                  ) : null}
                                </View>
                              ) : blockingGate ? (
                                <Text className="mt-2 text-xs font-medium text-text/60">
                                  Locked — pass deliverable {blockingGate.sequence} first
                                </Text>
                              ) : null}
                            </View>
                          ) : (
                            <View>
                              <Text
                                className="text-sm font-medium text-amber underline"
                                onPress={() => void onOpenSubmissionFile(sub.file_url)}
                              >
                                {sub.file_name}
                              </Text>
                              <View className="mt-1 flex-row flex-wrap items-center gap-2">
                                {sub.status === "late" ? (
                                  <Badge status="amber">Late</Badge>
                                ) : (
                                  <Badge status="success">Submitted</Badge>
                                )}
                                {canResubmit ? (
                                  <Button
                                    size="sm"
                                    variant="link"
                                    disabled={busy}
                                    onPress={() => void pickAndUploadDeliverable(d)}
                                  >
                                    Replace file
                                  </Button>
                                ) : null}
                              </View>
                              {d.allow_revisions === false ? (
                                <Text className="mt-2 text-xs text-text/50">
                                  Final — cannot be revised
                                </Text>
                              ) : isGraded ? (
                                <Text className="mt-2 text-xs text-text/50">
                                  Already graded — locked
                                </Text>
                              ) : isPastDue && d.allow_revisions ? (
                                <Text className="mt-2 text-xs text-text/50">Past due — locked</Text>
                              ) : null}
                              {justUploaded?.deliverableId === d.id ? (
                                <View
                                  className="mt-3 flex-row items-center gap-2.5 rounded-md border px-3 py-2"
                                  style={{
                                    borderColor: "rgba(143,187,138,0.55)",
                                    backgroundColor: STUDENT_THEME.successTintSoft,
                                  }}
                                >
                                  <View
                                    style={{
                                      width: 18,
                                      height: 18,
                                      borderRadius: 9,
                                      backgroundColor: COLORS.SAGE.hex,
                                      alignItems: "center",
                                      justifyContent: "center",
                                    }}
                                  >
                                    <Text
                                      style={{
                                        color: COLORS.FOREST.hex,
                                        fontSize: 11,
                                        fontFamily: FONTS.bodyBold,
                                      }}
                                    >
                                      ✓
                                    </Text>
                                  </View>
                                  <View className="min-w-0 flex-1">
                                    <Text
                                      className="text-[12.5px] text-forest"
                                      style={{ fontFamily: FONTS.bodySemiBold }}
                                    >
                                      Submitted at{" "}
                                      {new Date(justUploaded.at).toLocaleTimeString(undefined, {
                                        hour: "numeric",
                                        minute: "2-digit",
                                      })}
                                      {" · confirmed"}
                                    </Text>
                                    <Text
                                      className="text-[11.5px]"
                                      style={{
                                        fontFamily: FONTS.body,
                                        color: STUDENT_THEME.textOnSurfaceDim,
                                      }}
                                    >
                                      {d.allow_revisions
                                        ? "Replace anytime before the deadline."
                                        : "Final — locked in."}
                                    </Text>
                                  </View>
                                </View>
                              ) : null}
                            </View>
                          )}
                        </View>
                      </Card>
                    </View>
                    {blockingGate ? (
                      <View
                        pointerEvents="none"
                        style={{
                          position: "absolute",
                          left: 0,
                          right: 0,
                          top: 0,
                          bottom: 0,
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <View
                          className="flex-row items-center gap-2 rounded-md border px-3 py-1.5"
                          style={{
                            borderColor: "rgba(181,135,58,0.55)",
                            backgroundColor: "rgba(28,43,26,0.92)",
                          }}
                        >
                          <Padlock closed color={COLORS.AMBER.hex} size={13} />
                          <Text
                            className="text-[11px] uppercase"
                            style={{
                              fontFamily: FONTS.bodyBold,
                              letterSpacing: 1.4,
                              color: COLORS.LINEN.hex,
                            }}
                          >
                            Pass deliverable {blockingGate.sequence} to unlock
                          </Text>
                        </View>
                      </View>
                    ) : null}
                  </View>
                </DeliverableDropZone>
              );
            })}
          </View>
        </View>
      ) : (
        <View className="bg-linen px-4 py-4 md:px-8">
          <Card variant="light" padding="md">
            <EmptyState
              title="No deliverables yet"
              subtitle="Deliverables will appear here once competition admins publish them."
            />
          </Card>
        </View>
      )}
    </ScrollView>
  );
}

